import { Instant, LocalDate, ZoneId } from "@js-joda/core";
import { createInstant } from "./ApiConverters";
import { localDateToInstantUtc, showBrowserDate, showDate } from "./Date";

export function getMinCancelParkerDate(): string {
  return showBrowserDate(LocalDate.now());
}

function parseRemovedWhen(removedWhen: Instant | string): Instant {
  return typeof removedWhen === "string" ? createInstant(removedWhen) : removedWhen;
}

/** Calendar cancellation day from stored RemovedWhen (UTC midnight). */
export function cancellationInstantToLocalDate(
  removedWhen: Instant | string,
  _facilityTimeZone?: string
): LocalDate {
  return parseRemovedWhen(removedWhen).atZone(ZoneId.of("UTC")).toLocalDate();
}

/** yyyy-MM-dd for date inputs from stored RemovedWhen. */
export function cancellationInstantToPickerDate(
  removedWhen: Instant | string,
  facilityTimeZone?: string
): string {
  return showBrowserDate(cancellationInstantToLocalDate(removedWhen, facilityTimeZone));
}

/** Default cancel date: day before access period ends, not before today. */
export function defaultCancellationDateFromAccessPeriodEnd(
  accessPeriodEnd: string,
  notBefore?: string
): string {
  const today = notBefore ?? getMinCancelParkerDate();
  const dayBefore = showBrowserDate(LocalDate.parse(accessPeriodEnd).minusDays(1));
  return dayBefore >= today ? dayBefore : today;
}

export function getDefaultCancelParkerDate(options: {
  removedWhen?: Instant | string | null;
  accessPeriodEnd?: string | null;
  facilityTimeZone: string;
}): string {
  if (options.removedWhen) {
    return cancellationInstantToPickerDate(options.removedWhen, options.facilityTimeZone);
  }

  const today = getMinCancelParkerDate();
  const accessEnd = options.accessPeriodEnd;
  if (accessEnd) {
    return defaultCancellationDateFromAccessPeriodEnd(accessEnd, today);
  }
  return today;
}

export function isCancelDateToday(cancellationDate: string): boolean {
  return cancellationDate === getMinCancelParkerDate();
}

export function isEffectiveCancellation(
  removedWhen: Instant | string | null | undefined,
  facilityTimeZone?: string
): boolean {
  if (!removedWhen) return false;
  return cancellationInstantToPickerDate(removedWhen, facilityTimeZone) <= getMinCancelParkerDate();
}

export function formatParkerCancelDate(
  removedWhen: Instant | string | null | undefined,
  facilityTimeZone?: string
): string {
  if (!removedWhen) return "—";
  return showDate(cancellationInstantToLocalDate(removedWhen, facilityTimeZone));
}

export function toRemovedWhenInstant(cancellationDate: string): Instant {
  return createInstant(localDateToInstantUtc(cancellationDate));
}
